import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";

import { assignCreditsToPartner, getAllPartners, getAssignableFarmFieldMethods } from "services";

import { truncateCredits, useCancelAsyncOnUnmount } from "utils";

import { AdminHead, AdminTable, MainPage, Tabs, Message as message } from "components";

import { MethodsAssignmentDrawer } from "pages/admin/partners/components/MethodsAssignmentDrawer";

export const AssignMethodCredits = () => {
  const [currentFarmFieldMethod, setCurrentFarmFieldMethod] = useState(null);
  const [currentPartner, setCurrentPartner] = useState(null);
  const [farmFieldMethods, setFarmFieldMethods] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showMethodAssignmentDrawer, setShowMethodAssignmentDrawer] = useState(false);
  const [quantity, setQuantity] = useState(0);
  const [assignableMethods, setAssignableMethods] = useState([]);
  const signal = useCancelAsyncOnUnmount()();
  const [methodPage, setMethodPage] = useState(0);

  const form = React.createRef();

  const loadFarmFieldMethods = useCallback(() => {
    getAssignableFarmFieldMethods({
      page: methodPage,
      signal,
    }).then((res) => {
      setFarmFieldMethods([...farmFieldMethods, ...res.methods]);

      if (res.count > methodPage * 250 && res.count > 250) {
        setMethodPage(methodPage + 1);
      }
    });
  }, [signal]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPartners = useCallback(() => {
    getAllPartners({
      signal,
    })
      .then((res) => {
        setPartners(res.partners.filter((partner) => !partner.disabled));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [signal]);

  useEffect(() => {
    const data = farmFieldMethods
      .filter((fieldMethod) => fieldMethod.assignedCredits < fieldMethod.totalCredits)
      .sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf());

    for (let i = 0; i < data.length; i += 1) {
      data[i].assignableCredits = data[i].totalCredits - data[i].assignedCredits;
    }
    setAssignableMethods(data);
  }, [farmFieldMethods]);

  useEffect(() => {
    if (form.current) {
      form.current.setFieldsValue({
        quantity: quantity,
      });
    }
    // eslint-disable-next-line
  }, [quantity]);

  const handleCloseMethodAssignmentDrawer = () => {
    loadFarmFieldMethods();
    setCurrentPartner(null);
    setShowMethodAssignmentDrawer(false);
    setCurrentFarmFieldMethod(null);
  };

  const handleOpenAssignmentDrawer = (farmFieldMethod) => {
    setShowMethodAssignmentDrawer(true);
    setCurrentFarmFieldMethod(farmFieldMethod);
  };

  const handlePartnerChange = (partnerId) => {
    const newPartner = partners.find((partner) => partner.id === partnerId);
    setCurrentPartner(newPartner);
    const credits = currentFarmFieldMethod.totalCredits - currentFarmFieldMethod.assignedCredits;

    setQuantity(truncateCredits(credits));
  };

  const complete = (v) => {
    if (v.quantity === 0) {
      message({
        content: "Quantity can not be 0",
        icon: "gremlin",
      });
      return;
    }

    if (v.creditType === "Insetting" && v.partnerId !== currentFarmFieldMethod.farmPartnerId) {
      message({
        content: "You can only assign credits as insetting, when the farmer is assigned to the partner",
        icon: "gremlin",
      });
      return;
    }

    setButtonLoading(true);
    const data = {
      farmId: currentFarmFieldMethod.farmId,
      fieldId: currentFarmFieldMethod.fieldId,
      completed: currentFarmFieldMethod.completed,
      id: currentFarmFieldMethod.id,
      type: "MP",
    };

    assignCreditsToPartner({
      credits: quantity,
      type: data.type,
      farmFieldMethodOrModuleId: data.id,
      partnerId: v.partnerId,
      creditType: v.creditType,
      farmId: data.farmId,
      fieldId: data.fieldId,
      completed: data.completed,
      signal,
    })
      .then(() => {
        handleCloseMethodAssignmentDrawer();
        setCurrentFarmFieldMethod(null);
        setCurrentPartner(null);
        setFarmFieldMethods([]);
        setMethodPage(0);
        loadFarmFieldMethods();
        loadPartners();
        setButtonLoading(false);
        message({
          content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
          icon: "gremlin",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (methodPage > 0) {
      loadFarmFieldMethods();
    }
  }, [loadFarmFieldMethods, methodPage]);

  useEffect(() => {
    loadFarmFieldMethods();
    loadPartners();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remainingPartnerCredits = currentPartner ? currentPartner.remainingCredits - quantity : 0;
  const remainingMethodCredits = currentFarmFieldMethod ? currentFarmFieldMethod.totalCredits - currentFarmFieldMethod.assignedCredits - quantity : 0;

  return (
    <MainPage loading={loading} pageName={"admin"}>
      <AdminHead
        headline={`${assignableMethods.length} unnassigned method credits`}
        description="You can assign methods to partners either partially, or in full, here. Assigned points will be deducted from the partner's remaining credits. 1 credit equals 1000 points / kgs of sequestered carbon."
      />
      <Tabs
        items={[
          {
            label: "Methods",
            key: "methods",
            children: assignableMethods.length > 0 && (
              <AdminTable
                dataSource={assignableMethods}
                config={{
                  actions: [
                    {
                      icon: "klim/plus",
                      func: handleOpenAssignmentDrawer,
                      tooltip: "Assign these credits",
                    },
                  ],
                  exclude: ["totalCredits", "nextTask", "methodName", "methodId", "fieldName", "fieldId", "assignedCredits", "id", "farmPartnerId"],
                }}
              />
            ),
          },
          {
            label: "Partners",
            key: "partners",
            children: partners.length > 0 && (
              <AdminTable
                dataSource={partners}
                config={{
                  exclude: [
                    "remainingCredits",
                    "referralToken",
                    "pointPriceCategory1",
                    "pointPriceCategory2",
                    "pointPriceCategory3",
                    "pointPriceCategory4",
                    "maxHectares",
                    "disabled",
                    "contactPhone",
                    "contactName",
                    "contactEmail",
                  ],
                }}
              />
            ),
          },
        ]}
      />

      {currentFarmFieldMethod && showMethodAssignmentDrawer && (
        <MethodsAssignmentDrawer
          showAssignmentDrawer={showMethodAssignmentDrawer}
          handleCloseAssignmentDrawer={handleCloseMethodAssignmentDrawer}
          form={form}
          complete={complete}
          currentFarmFieldMethod={currentFarmFieldMethod}
          remainingMethodCredits={remainingMethodCredits}
          handlePartnerChange={handlePartnerChange}
          partners={partners}
          currentPartner={currentPartner}
          remainingPartnerCredits={remainingPartnerCredits}
          quantity={quantity}
          setQuantity={setQuantity}
          type={"method"}
          loading={buttonLoading}
        />
      )}
    </MainPage>
  );
};
